<template>
  <ViewerLayoutRoot class="content-guest-layout" data-testid="content-guest-layout">
    <GuestTopBar compact-desktop-top-spacing />

    <div class="content-guest-layout__content">
      <div ref="contentRoot" data-testid="content-guest-content">
        <ViewerContent :max-height="contentMaxHeight" />
      </div>
      <div v-if="isMobile" ref="mobileNameRoot" class="px-4 pt-4 pb-1" data-testid="content-guest-mobile-name">
        <ItemName />
      </div>
      <div ref="metadataRoot" class="px-4 sm:px-0" data-testid="content-guest-metadata">
        <MetadataSection :show-toolbar="false" />
      </div>
    </div>

    <!-- Engagement Bar -->
    <div ref="engagementRoot" class="px-4 sm:px-0" data-testid="content-guest-engagement">
      <EngagementBar />
    </div>

    <!-- Tabs -->
    <div class="content-guest-layout__tabs px-4 sm:px-0">
      <ContentGuestTabs @addedComment="emit('addedComment')" />
    </div>
  </ViewerLayoutRoot>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import ViewerContent from '@/modules/viewer/components/Content.vue';
import ItemName from '@/modules/viewer/components/Name.vue';
import { useMobileDetection } from '@/composables/useMobileDetection';
import GuestTopBar from '../../shared/GuestTopBar.vue';
import EngagementBar from '../../shared/EngagementBar.vue';
import MetadataSection from '../../shared/MetadataSection.vue';
import ContentGuestTabs from './ContentGuestTabs.vue';

import ViewerLayoutRoot from '../../shared/ViewerLayoutRoot.vue';

const MIN_CONTENT_HEIGHT_PX = 320;

const { isMobile } = useMobileDetection();
const contentMaxHeight = ref<string>();
const contentRoot = ref<HTMLElement | null>(null);
const mobileNameRoot = ref<HTMLElement | null>(null);
const metadataRoot = ref<HTMLElement | null>(null);
const engagementRoot = ref<HTMLElement | null>(null);
let controlsResizeObserver: ResizeObserver | undefined;
let resizeRafId: number | undefined;
let isUnmounted = false;

const emit = defineEmits<{
  addedComment: [];
}>();

const updateContentMaxHeight = () => {
  if (!contentRoot.value || !metadataRoot.value || !engagementRoot.value || (isMobile.value && !mobileNameRoot.value)) {
    contentMaxHeight.value = undefined;
    return;
  }

  // Keep the height budget stable when a mobile browser resizes after the page has scrolled.
  const contentTop = contentRoot.value.getBoundingClientRect().top + window.scrollY;
  const available =
    window.innerHeight -
    contentTop -
    (mobileNameRoot.value?.getBoundingClientRect().height ?? 0) -
    metadataRoot.value.getBoundingClientRect().height -
    engagementRoot.value.getBoundingClientRect().height -
    24;

  // Avoid Image.vue's 80vh fallback so the engagement controls remain reachable on short viewports.
  // Never shrink below MIN_CONTENT_HEIGHT_PX — on very short viewports the page scrolls to reach
  // engagement controls instead of squeezing the image past legibility.
  contentMaxHeight.value = `${Math.max(MIN_CONTENT_HEIGHT_PX, Math.floor(available))}px`;
};

const scheduleContentMaxHeightUpdate = () => {
  if (resizeRafId !== undefined) {
    cancelAnimationFrame(resizeRafId);
  }
  resizeRafId = requestAnimationFrame(updateContentMaxHeight);
};

const observeControls = () => {
  if (!window.ResizeObserver || !metadataRoot.value || !engagementRoot.value) {
    return;
  }

  controlsResizeObserver?.disconnect();
  controlsResizeObserver = new ResizeObserver(scheduleContentMaxHeightUpdate);
  if (mobileNameRoot.value) {
    controlsResizeObserver.observe(mobileNameRoot.value);
  }
  controlsResizeObserver.observe(metadataRoot.value);
  controlsResizeObserver.observe(engagementRoot.value);
};

watch(isMobile, async () => {
  await nextTick();
  if (isUnmounted) {
    return;
  }

  scheduleContentMaxHeightUpdate();
  observeControls();
});

onMounted(async () => {
  await nextTick();
  if (isUnmounted) {
    return;
  }

  updateContentMaxHeight();
  observeControls();
  window.addEventListener('resize', scheduleContentMaxHeightUpdate);
});

onUnmounted(() => {
  isUnmounted = true;
  window.removeEventListener('resize', scheduleContentMaxHeightUpdate);
  controlsResizeObserver?.disconnect();
  if (resizeRafId !== undefined) {
    cancelAnimationFrame(resizeRafId);
  }
});
</script>

<style scoped>
.content-guest-layout__content {
  @apply flex flex-col min-w-0;
}

.content-guest-layout__tabs {
  @apply mt-2;
}
</style>
